import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, ArrowLeftRight, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Badge } from './ui/badge';

const DocumentCompareModal = ({ isOpen, onClose, documents, selectedDocs, setSelectedDocs }) => {
  const { toast } = useToast();
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);

  const toggleSelection = (docId) => {
    setSelectedDocs(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else if (prev.length < 5) {
        return [...prev, docId];
      } else {
        toast({
          title: 'Maximum Reached',
          description: 'You can compare up to 5 documents at once',
          variant: 'default'
        });
        return prev;
      }
    });
  };

  const handleCompare = async () => {
    if (selectedDocs.length < 2) {
      toast({
        title: 'Selection Required',
        description: 'Please select at least 2 documents to compare',
        variant: 'destructive'
      });
      return;
    }

    setComparing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/work/documents/compare`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ document_ids: selectedDocs })
        }
      );

      if (!response.ok) {
        throw new Error('Comparison failed');
      }

      const data = await response.json();
      setComparisonResult(data.response);

      toast({
        title: '✅ Comparison Complete!',
        description: 'Review the analysis below'
      });
    } catch (error) {
      console.error('Comparison error:', error);
      toast({
        title: 'Comparison Failed',
        description: 'Unable to compare documents. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setComparing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-blue-600" />
            Compare Documents
          </DialogTitle>
        </DialogHeader>

        {!comparisonResult ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Select 2-5 documents to compare ({selectedDocs.length} selected)
              </p>
              <Badge variant={selectedDocs.length >= 2 ? "default" : "secondary"}>
                {selectedDocs.length}/5
              </Badge>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {documents.map((doc) => {
                const isSelected = selectedDocs.includes(doc.id);
                return (
                  <div
                    key={doc.id}
                    onClick={() => toggleSelection(doc.id)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {isSelected && <CheckCircle className="h-4 w-4 text-blue-600" />}
                          <h4 className="font-medium text-gray-900">{doc.title}</h4>
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-1">{doc.summary}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {doc.template || 'general'}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleCompare}
              disabled={comparing || selectedDocs.length < 2}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {comparing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Comparing... (10-15 credits)
                </>
              ) : (
                <>
                  <ArrowLeftRight className="h-4 w-4 mr-2" />
                  Compare Selected Documents
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800 font-medium">✅ Comparison Complete</p>
            </div>

            <div className="bg-white border rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                {comparisonResult}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setComparisonResult(null);
                  setSelectedDocs([]);
                }}
                variant="outline"
                className="flex-1"
              >
                Compare More
              </Button>
              <Button onClick={onClose} className="flex-1">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentCompareModal;
